import React, { useState, useEffect } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { ordersAPI } from '../../services/api';
import PeriodFilter from '../orders/PeriodFilter';
import { exportOrdersExcel, excelDate } from '../orders/exportOrders';
import { toast } from 'react-toastify';
import { formatCurrency, formatNumber } from '../../utils/currency';
import { formatTableDate } from '../../utils/dateFormatter';

const PAGE_SIZE = 500;

export function OrderList() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(todayStr);
  const [loadedRange, setLoadedRange] = useState([firstOfMonth, todayStr]);

  useEffect(() => { fetchOrders(); fetchStats(); }, []);

  const fetchOrders = async (start = startDate, end = endDate) => {
    try {
      setLoading(true);
      const params = {};
      if (start && end) { params.start_date = start; params.end_date = end + ' 23:59:59'; }
      const response = await ordersAPI.getAll(params);
      setOrders(response.data.orders);
      setLoadedRange([start, end]);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (start = startDate, end = endDate) => {
    try {
      const params = {};
      if (start && end) { params.start_date = start; params.end_date = end + ' 23:59:59'; }
      const response = await ordersAPI.getStats(params);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const handleFilter = (s = startDate, e = endDate) => { setStartDate(s); setEndDate(e); fetchOrders(s, e); fetchStats(s, e); };
  const handleDownload = () => {
    if (!filteredOrders.length) return toast.info('No orders to download for this period');
    exportOrdersExcel({
      title: 'Shopify Orders', orders: filteredOrders, startDate: loadedRange[0], endDate: loadedRange[1],
      orderKey: o => o.shopify_order_id,
      columns: [
        { header: 'Order #', value: o => o.order_number, width: 18 },
        { header: 'Date', value: o => excelDate(o.order_date), width: 18 },
        { header: 'Product', value: o => o.product_title, width: 50 },
        { header: 'SKU', value: o => o.sku, width: 18 },
        { header: 'Qty', value: o => o.quantity, sum: true, width: 6 },
        { header: 'Gross', value: o => o.gross_revenue, money: true },
        { header: 'Discount', value: o => o.discount_amount, money: true },
        { header: 'Net Revenue', value: o => o.selling_price, money: true },
        { header: 'Cost', value: o => o.cost_price, money: true },
        { header: 'Fees', value: o => o.shopify_fees, money: true },
        { header: 'Profit', value: o => o.net_profit, money: true },
        { header: 'Payment', value: o => o.financial_status },
        { header: 'Fulfilment', value: o => o.fulfillment_status },
      ],
    });
  };
  const handleClearFilter = () => { setStartDate(''); setEndDate(''); fetchOrders('', ''); fetchStats('', ''); };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const response = await ordersAPI.sync({});
      toast.success(`Synced ${response.data.synced} order items!`);
      fetchOrders(); fetchStats();
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getProfitMargin = () => {
    if (!stats || !stats.total_sales || stats.total_sales === 0) return '0.0';
    return ((parseFloat(stats.total_profit || 0) / parseFloat(stats.total_sales)) * 100).toFixed(1);
  };

  const filteredOrders = orders.filter(order => {
    const q = search.toLowerCase();
    return (
      order.product_title?.toLowerCase().includes(q) ||
      order.order_number?.toString().toLowerCase().includes(q) ||
      order.sku?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(filteredOrders.length / PAGE_SIZE);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Orders</h1>
          <p className="text-gray-600">
            {search ? `${filteredOrders.length} of ${orders.length} order items` : `${orders.length} order items displayed`}
          </p>
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg disabled:opacity-50">
          <RefreshCw className={`mr-2 ${syncing ? 'animate-spin' : ''}`} size={20} />
          {syncing ? 'Syncing...' : 'Sync Orders'}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-4 min-w-0">
            <p className="text-gray-600 mb-2 text-sm">Total Orders</p>
            <p className="text-xl lg:text-2xl font-bold text-purple-600 break-words">{formatNumber(stats.total_orders)}</p>
            <p className="text-xs text-gray-500 mt-1">{formatNumber(stats.total_items)} units sold</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 min-w-0">
            <p className="text-gray-600 mb-2 text-sm">Gross Revenue</p>
            <p className="text-xl lg:text-2xl font-bold text-slate-500 break-words">{formatCurrency(stats.total_gross_revenue)}</p>
            <p className="text-xs text-gray-400 mt-1">before discounts</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 min-w-0">
            <p className="text-gray-600 mb-2 text-sm">Net Revenue</p>
            <p className="text-xl lg:text-2xl font-bold text-blue-600 break-words">{formatCurrency(stats.total_sales)}</p>
            <p className="text-xs text-gray-400 mt-1">actually collected</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 min-w-0">
            <p className="text-gray-600 mb-2 text-sm">Total Profit</p>
            <p className="text-xl lg:text-2xl font-bold text-green-600 break-words">{formatCurrency(stats.total_profit)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 min-w-0">
            <p className="text-gray-600 mb-2 text-sm">Profit Margin</p>
            <p className="text-xl lg:text-2xl font-bold text-orange-600 break-words">{getProfitMargin()}%</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <PeriodFilter startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}
            onApply={handleFilter} color="blue" />
          <button onClick={handleClearFilter}
            className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium">
            Show All
          </button>
          <button onClick={handleDownload} disabled={loading}
            className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50">
            <Download size={16} /> Download Excel
          </button>
          <div className="flex-1 min-w-48 relative">
            <label className="block text-gray-600 text-sm font-medium mb-1">Search</label>
            <Search className="absolute left-3 bottom-2.5 text-gray-400" size={18} />
            <input type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              placeholder="Product name, order #, or SKU..."
              className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            {search && (
              <button onClick={() => { setSearch(''); setCurrentPage(1); }}
                className="absolute right-3 bottom-2.5 text-gray-400 hover:text-gray-600">✕</button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="w-full overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="animate-spin text-blue-600 mr-2" size={24} />
              <span className="text-gray-500">Loading orders...</span>
            </div>
          ) : paginatedOrders.length > 0 ? (
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[6%]" />
                <col className="w-[20%]" />
                <col className="w-[9%]" />
                <col className="w-[4%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[7%]" />
                <col className="w-[8%]" />
                <col className="w-[11%]" />
              </colgroup>
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="text-left py-3 px-2 text-gray-600 font-semibold">Order #</th>
                  <th className="text-left py-3 px-2 text-gray-600 font-semibold">Product</th>
                  <th className="text-left py-3 px-2 text-gray-600 font-semibold">SKU</th>
                  <th className="text-center py-3 px-2 text-gray-600 font-semibold">Qty</th>
                  <th className="text-right py-3 px-2 text-gray-600 font-semibold">Gross</th>
                  <th className="text-right py-3 px-2 text-gray-600 font-semibold">Discount</th>
                  <th className="text-right py-3 px-2 text-gray-600 font-semibold">Net Rev.</th>
                  <th className="text-right py-3 px-2 text-gray-600 font-semibold">Cost</th>
                  <th className="text-right py-3 px-2 text-gray-600 font-semibold">Fees</th>
                  <th className="text-right py-3 px-2 text-gray-600 font-semibold">Profit</th>
                  <th className="text-left py-3 px-2 text-gray-600 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order) => (
                  <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors align-top">
                    <td className="py-3 px-2 font-medium text-gray-800 break-words">{order.order_number}</td>
                    <td className="py-3 px-2 text-gray-700 break-words">{order.product_title}</td>
                    <td className="py-3 px-2 break-words">
                      {order.sku ? (
                        <span className="font-mono text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded inline-block break-all">
                          {order.sku}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-center font-medium">{order.quantity}</td>
                    <td className="py-3 px-2 text-right text-slate-500 break-words">{formatCurrency(order.gross_revenue)}</td>
                    <td className="py-3 px-2 text-right text-red-500 break-words">
                      {order.discount_amount > 0 ? `-${formatCurrency(order.discount_amount)}` : formatCurrency(0)}
                    </td>
                    <td className="py-3 px-2 text-right font-semibold text-blue-600 break-words">{formatCurrency(order.selling_price)}</td>
                    <td className="py-3 px-2 text-right text-gray-700 break-words">{formatCurrency(order.cost_price)}</td>
                    <td className="py-3 px-2 text-right text-orange-600 break-words">{formatCurrency(order.shopify_fees)}</td>
                    <td className="py-3 px-2 text-right font-bold text-green-600 break-words">{formatCurrency(order.net_profit)}</td>
                    <td className="py-3 px-2 text-gray-600 break-words">{formatTableDate(order.order_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              {search ? (
                <><p className="text-gray-500 text-lg mb-2">No orders found for "{search}"</p>
                <p className="text-gray-400 text-sm">Try a different search term</p></>
              ) : (
                <><p className="text-gray-500 text-lg mb-4">No orders found</p>
                <p className="text-gray-400 text-sm">Try adjusting the date filter or sync orders</p></>
              )}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length} orders
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40">
                <ChevronLeft size={18} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx-1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                .map((p, idx) => p === '...' ? (
                  <span key={`e-${idx}`} className="px-2 text-gray-400">...</span>
                ) : (
                  <button key={p} onClick={() => setCurrentPage(p)}
                    className={`px-3 py-1 rounded-lg border text-sm font-medium ${currentPage === p ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-100 text-gray-700'}`}>
                    {p}
                  </button>
                ))}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-40">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderList;